import { defineManifest } from '@ndrc/app-sdk/manifest';

/**
 * Tableur — la grille brute des contacts, séance 1 de la progression décidée par l'enseignant.
 *
 * Cette app ne fournit AUCUNE interprétation : ni score, ni température, ni segment, ni catégorie
 * toute faite. Elle donne la matière (les lignes du fichier source, imperfections comprises) et
 * les gestes d'un tableur — corriger une cellule, trier, filtrer, **créer ses propres colonnes**.
 * Décider ce qui rend un prospect prioritaire est le travail de l'élève, jamais celui de l'outil.
 *
 * Les contacts vivent dans les entités communes du cœur (`core.contacts`,
 * `core.accounts`), lues et écrites par `ctx.data` sous la RLS de l'espace de l'élève. Les reçus d'import
 * sont propres à l'application et garantissent leur reprise. Les colonnes
 * inventées par l'élève sont rangées dans le champ ouvert `attributes` du contact — jamais en dur
 * dans le schéma (ADR-008).
 *
 * `contacts.write` et `contacts.delete` permettent de corriger et retirer les fiches.
 * `app.records.read/write` conserve les reçus d'import. `accounts.read` affiche les entreprises
 * et permet de choisir explicitement le rattachement des contacts importés.
 *
 * `contacts.delete` est arrivée le 7 septembre 2026, avec la décision du cœur qui l'autorise
 * (`PERMISSION_MATRIX`, `packages/core-domain/src/permissions.ts`). Sans elle, « supprimer une
 * ligne » ne pouvait être qu'un marquage privé à cette app — donc une fiche disparue ici et bien
 * vivante dans le CRM, la relecture et le tableau de l'enseignant. La suppression reste LOGIQUE
 * (`deleted_at`) : rien n'est effacé, « Annuler » ramène la fiche, l'enseignant la retrouve.
 *
 * **Son identité visuelle est à elle**, et c'est ce manifeste qui la porte — le cœur ne nomme
 * jamais une app (règle 1 de `CLAUDE.md`, §52 du cahier des charges). `color` vient de la palette
 * verrouillée (`docs/design/desktop-reference.md`, `--emer-i`) : le vert des tableurs, celui qu'un
 * élève reconnaît avant même de lire le nom. Une app ne choisit jamais une couleur hors de ce
 * fichier. `glyph` est sa marque : une feuille de calcul — bandeau d'en-tête plein, cadre, trois
 * colonnes, trois lignes — dessinée en blanc sur ce vert. Le `icon: 'grid'` reste déclaré comme
 * repli si le glyphe venait à disparaître.
 *
 * La couleur était `--indigo-i` jusqu'au 7 septembre 2026 ; l'enseignant a demandé « un faux
 * Excel », donc du vert et une grille.
 */
export const manifest = defineManifest({
  id: 'tableur',
  name: 'Tableur',
  description:
    'La grille brute du fichier de prospection : corriger, trier, filtrer, et créer ses propres colonnes.',
  icon: 'grid',
  color: '#047857',
  glyph: [
    // Le bandeau d'en-tête, plein : la ligne de titres d'un tableur.
    { d: 'M6.2 4.4h11.6a2 2 0 0 1 2 2v2H4.2v-2a2 2 0 0 1 2-2z', fill: true },
    // Le cadre de la feuille.
    { d: 'M6.2 4.4h11.6a2 2 0 0 1 2 2v11.2a2 2 0 0 1-2 2H6.2a2 2 0 0 1-2-2V6.4a2 2 0 0 1 2-2z' },
    // Deux séparateurs de colonnes et deux de lignes : une grille 3 × 3.
    { d: 'M9.4 8.4v11.2M14.6 8.4v11.2', width: 1.2, opacity: 0.85 },
    { d: 'M4.2 12.1h15.6M4.2 15.9h15.6', width: 1.2, opacity: 0.85 },
  ],
  version: '0.1.0',
  sdk: '0.0.0',
  status: 'dev',
  category: 'prospection',
  permissions: {
    uses: [
      'contacts.read',
      'contacts.write',
      'contacts.delete',
      'accounts.read',
      'app.records.read',
      'app.records.write',
    ],
    defines: [],
  },
  entities: {
    uses: [{ entity: 'contacts' }, { entity: 'accounts' }],
    owns: [
      {
        table: 'merges',
        scope: 'workspace',
        description: 'Reçus et versions conservées avant les fusions explicites.',
      },
      {
        table: 'imports',
        scope: 'workspace',
        description: 'Reçus des imports étudiants, pour reprise sans doublon.',
      },
    ],
  },
  events: {
    emits: [],
    subscribes: [],
  },
  ui: {
    student: {
      defaultRoute: '/',
      routes: [{ path: '/', title: 'Grille' }],
      layout: 'full',
    },
  },
  migrations: { schema: 'app_tableur', dir: 'migrations' },
});
